/** Shared state + step config for the guided domain setup wizard. */
import { brand } from '@/config/brand';

export interface DomainDraft {
  domain: string;
  origin: string;
  originScheme: 'https' | 'http';
  sslMode: 'flexible' | 'full' | 'strict';
  minTls: '1.2' | '1.3';
  /**
   * WAF master switch. Defaults true — new domains start protected with managed
   * OWASP rules. Enforcement mode (Monitor/Block), bot and AI rules are tuned
   * later from the domain's Security → WAF tab, not during setup.
   */
  wafEnabled: boolean;
}

export const DEFAULT_DRAFT: DomainDraft = {
  domain: '',
  origin: '',
  originScheme: 'https',
  sslMode: 'full',
  minTls: '1.3',
  wafEnabled: true,
};

export interface WizardStep {
  id: 'origin' | 'dns' | 'ssl' | 'waf';
  label: string;
  title: string;
  description: string;
  /** Step may be skipped (advances without input). */
  skippable: boolean;
}

export const STEPS: WizardStep[] = [
  {
    id: 'origin',
    label: 'Origin',
    title: 'Add your domain',
    description: 'An app groups your traffic at the edge. Tell us the domain and where to fetch content from.',
    skippable: false,
  },
  {
    id: 'dns',
    label: 'DNS',
    title: 'Point DNS to the edge',
    description: `Create the record below at your DNS provider so visitors resolve to ${brand.shortName}.`,
    skippable: true,
  },
  {
    id: 'ssl',
    label: 'SSL / TLS',
    title: 'Secure the connection',
    description: 'Choose how traffic between the edge and your origin is encrypted.',
    skippable: true,
  },
  {
    id: 'waf',
    label: 'WAF',
    title: 'Turn on WAF',
    description: 'Protect your domain from web attacks with managed OWASP rules, bot and DDoS protection.',
    skippable: true,
  },
];

export const SSL_MODES: { value: DomainDraft['sslMode']; label: string; hint: string }[] = [
  { value: 'flexible', label: 'Flexible', hint: 'Encrypts visitor↔edge only; edge↔origin stays HTTP.' },
  { value: 'full', label: 'Full', hint: 'Encrypts end-to-end; origin may use a self-signed cert.' },
  { value: 'strict', label: 'Full (strict)', hint: 'End-to-end with a valid, trusted origin certificate.' },
];
